import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppState } from '../../AppStateProvider';
import type { WidgetItem } from '../../domain/company_widgets';
import { SELECTED_COMPANY_OVERVIEW_WIDGETS } from '../../domain/user';
import { useUserQuery } from '../../features/user/useUserQuery';
import { useUserSettings } from '../../features/user/useUserSettings';
import { appStore } from '../../services/appStore';
import CompanyWidgetsService from '../../services/companyWidgets';
import { translate } from '../../services/i18n';

export const useCompanyOverviewWidgets = () => {
  const { getUserSetting, setUserSettings } = useUserSettings();
  const { widgetId: widgetIdToScroll } = useAppState();
  const { data: user } = useUserQuery();

  const [widgets, setWidgets] = useState<WidgetItem[]>(() => {
    const widgetsFromStorage = getUserSetting(SELECTED_COMPANY_OVERVIEW_WIDGETS);
    const defaultWidgets = CompanyWidgetsService.getDefaultCompanyWidgets(user);
    const allWidgets = CompanyWidgetsService.getAllWidgetsIds();

    if (CompanyWidgetsService.isWidgetItemArray(widgetsFromStorage)) {
      const missingWidgetsInStorage = allWidgets.filter(
        (id) => !widgetsFromStorage.some(({ id: idFromStorage }) => id === idFromStorage)
      );
      const redundantWidgetsInStorage = widgetsFromStorage.filter(({ id }) => !allWidgets.includes(id));

      return [
        ...widgetsFromStorage.filter(
          ({ id }) => !redundantWidgetsInStorage.some(({ id: idFromStorage }) => id === idFromStorage)
        ),
        ...missingWidgetsInStorage.map((id) => ({ id, isChecked: false })),
      ];
    } else {
      const selectedWidgets = defaultWidgets.map((id) => ({ id, isChecked: true }));
      const remainingWidgets = allWidgets
        .filter((id) => !selectedWidgets.some(({ id: selectedId }) => id === selectedId))
        .map((id) => ({ id, isChecked: false }));

      return [...selectedWidgets, ...remainingWidgets];
    }
  });

  const saveSelectedWidgetsToStorage = (widgets: WidgetItem[]) => {
    setUserSettings({ [SELECTED_COMPANY_OVERVIEW_WIDGETS]: widgets });
  };

  const onSaveWidgets = useCallback((widgets: WidgetItem[]) => {
    setWidgets(widgets);
    saveSelectedWidgetsToStorage(widgets);
    appStore.setToast({ content: translate('company.widget_picker.success') });
  }, []);

  const selectedWidgets = useMemo(() => widgets.filter(({ isChecked }) => isChecked), [widgets]);

  useEffect(() => {
    if (widgetIdToScroll && !widgets.find((widget) => widget.id === widgetIdToScroll)?.isChecked) {
      const updatedWidgets = widgets.map((widget) =>
        widget.id === widgetIdToScroll ? { ...widget, isChecked: true } : widget
      );
      saveSelectedWidgetsToStorage(updatedWidgets);
      setWidgets(updatedWidgets);
    }
  }, [widgetIdToScroll]);

  return {
    allWidgets: widgets,
    onSaveWidgets,
    selectedWidgets,
  };
};
